import './externs'

/** @implements {maurice.Parallax} */
export default class Parallax {
  constructor(el, parent) {
    this._disabled = false

    /** @type {Element} */
    this.el = el // the IO target element
    this.listener = null

    this._speedy = 0
    this._speedx = 0

    this.speedxoverride = null
    this._speedyoverride = null
    this.firstRender = true

    /** Whether scrolling only once from bottom to top []. @type {boolean} */
    this.once = false

    // work on element without IO
    this.handle = this.el.nextElementSibling
    const noscript = this.handle.nextElementSibling

    this.maxScrollY = null
    this.maxX = null
    this.maxY = null

    // cleanup DOM
    if (noscript.tagName != 'NOSCRIPT') {
      console.warn('Could not remove noscript element.')
    } else parent.removeChild(noscript)

    this.el['parallax'] = this
    /** @type {!Function} */
    this.cancelDisabling = null
    /** @type {!Function} */
    this.cancelEnabling = null

    /** Last known window inner height. */
    this.innerHeight = null
    /** Last known element height. */
    this.height = null

    this._minHeightOverride = null
    this.maxYOverride = null
    /** The actual IMG element with full with and height.
     * @type {HTMLImageElement} */
    this.img = null
    this.heightAtDisable = null
    this.speedyAtDisable = null
    this.hash = ''

    this.translateX = 0
    this.translateY = 0
  }
  /** Returns the speedy set on the component,
   * or computed speedy if component is to scroll only once. */
  get speedy() {
    if (this.once) {
      return this.speedOnceY
    }
    return this._speedy
  }
  get speedx() { return this._speedx }
  get enabled() { return !this._disabled }
  disable() {
    this._disabled = true
  }
  enable() {
    this._disabled = false
  }
  graduallyDisable(timeout = 500, interval = 16) {
    if (this.cancelDisabling) return
    this.disable()

    const heights = this.cacheMeasurements()
    const { height, maxScrollY, width } = heights

    this.heightAtDisable = height
    this.speedyAtDisable = this.speedy

    const speedyFrom = this.speedy
    const speedyTo = 0

    const maxYFrom = this.getMaxY(maxScrollY, speedyFrom)
    const maxYTo = this.getMaxY(maxScrollY, speedyTo)

    const minHeightFrom = this.getMinHeightForSpeedYPX(height, maxYFrom)

    // assuming width stays the same...
    const rat = this.img.width / width
    const toHeight = this.img.height / rat

    const minHeightTo = this.getMinHeightForSpeedYPX(toHeight, maxYTo)

    const array = [
      [speedyFrom, speedyTo, 'speedyoverride'],
      [maxYFrom, maxYTo, 'maxY'],
      [minHeightFrom, minHeightTo, 'minHeight'],
    ]
    changeValues(timeout, interval, array, ([currentSpeedy, currentMaxY, currentMinHeight]) => {
      this.speedyoverride = currentSpeedy
      this.maxYOverride = currentMaxY
      this.minHeightOverride = currentMinHeight
      this.compute()
    })
  }
  set minHeightOverride(minHeightOverride) {
    console.log('setting min height override to %s', minHeightOverride)
    this._minHeightOverride = minHeightOverride
  }
  get minHeightOverride() {
    return this._minHeightOverride
  }
  graduallyEnable(timeout = 500, interval = 16) {
    if (this.cancelDisabling) this.cancelDisabling()

    if (this.cancelEnabling) return
    this.enable()

    const heights = this.cacheMeasurements()
    const { maxScrollY } = heights

    const speedyFrom = this.speedyoverride
    const speedyTo = this.speedyAtDisable // this.speedy

    const maxYFrom = this.maxYOverride
    const maxYTo = this.getMaxY(maxScrollY, speedyTo)

    const minHeightFrom = this.minHeightOverride

    // assuming no resize
    const minHeightTo = this.getMinHeightForSpeedYPX(this.heightAtDisable, maxYTo)

    const array = [
      [speedyFrom, speedyTo, 'speedyoverride'],
      [maxYFrom, maxYTo, 'maxY'],
      [minHeightFrom, minHeightTo, 'minHeight'],
    ]
    changeValues(timeout, interval, array, ([currentSpeedy, currentMaxY, currentMinHeight]) => {
      this.speedyoverride = currentSpeedy
      this.maxYOverride = currentMaxY
      this.minHeightOverride = currentMinHeight
      this.compute()
    }, () => {
      this.speedyoverride = null
      this.maxYOverride = null
      this.minHeightOverride = null
      this.compute()
    })
  }
  get speedyoverride() {
    return this._speedyoverride
  }
  set speedyoverride(value) {
    console.log('setting speedyoverride to %s', value)
    this._speedyoverride = value
  }
  poll(interval = 24, timeout = 1000) {
    const cancel = poll(timeout, interval, () => this.listener())
  }
  unrender() {
    if (this.listener) {
      window.removeEventListener('scroll', this.listener)
    }
  }
  getSpeedOnceY(innerHeight, height) {
    return (height / (innerHeight + height))
  }
  get speedOnceY() {
    if (!this.innerHeight || !this.height) this.cacheMeasurements()
    return this.getSpeedOnceY(this.innerHeight, this.height)
  }
  cacheMeasurements() {
    // use absoulutely positioned el to minimise reflow and prevent against resizes.
    const box = this.el.getBoundingClientRect()
    const { top, height, width } = box
    const { innerHeight } = window
    const maxScrollY = -(-innerHeight - height)

    this.innerHeight = innerHeight
    this.height = height
    this.maxScrollY = maxScrollY
    return { top, height, width, innerHeight, maxScrollY }
  }
  /**
   * Calculate the transform to shift the background image.
   */
  compute(cachedHeights = this.cacheMeasurements()) {
    const { top, height, width, innerHeight, maxScrollY } = cachedHeights
    const d = top - innerHeight

    const speedx = this.speedxoverride !== null ? this.speedxoverride : this.speedx
    const speedy = this.speedyoverride !== null ? this.speedyoverride : this.speedy
    const hash = [maxScrollY,speedx,speedy].join('-')

    if (this.hash !== hash) {
      const maxY = this.maxYOverride !== null ? this.maxYOverride : this.getMaxY(maxScrollY, speedy)
      const minHeight = px(this.minHeightOverride !== null
        ? this.minHeightOverride : this.getMinHeightForSpeedYPX(height, maxY))
      this.handle.style['min-height'] = minHeight

      // not accounting for horizontal scrolling yet... [or are we now]
      const maxX = maxScrollY * Math.abs(speedx)
      this.handle.style['min-width'] = px(width + maxX)

      // this.maxScrollY = maxScrollY
      this.hash = hash
      this.maxX = maxX
      this.maxY = maxY
    }

    let Y = d * speedy
    let X = d * speedx

    if (this.speedx < 0) X -= this.maxX
    if (this.speedy < 0) Y -= this.maxY

    const t = `translate3d(${floatToPx(X + this.translateX)}, ${floatToPx(Y + this.translateY)}, 0)`
    this.handle.style.transform = t
    this.handle.style.webkitTransform = t
  }
  /**
   *
   * @param {number} height
   * @param {number} maxY
   */
  getMinHeightForSpeedYPX(height, maxY) {
    // const maxY = maxScrollY * Math.abs(speedy)
    const minHeight = height + maxY
    return minHeight
  }
  getMaxY(maxScrollY, speedy) {
    const maxY = maxScrollY * Math.abs(speedy)
    return maxY
  }
  /**
   * @param {function(Error, *)} callback
   * @param {Element} el
   * @suppress {checkTypes}
   */
  static 'load'(callback, el, {
    'background-image': backgroundImage,
    'rel-image': relImage,
    splendid: { mount },
  }) {
    const img = document.createElement('img')
    const src = relImage || `${mount}${backgroundImage}`
    img.src = src
    img.onload = () => {
      // const imgwidth = img.width
      callback(null, { img, src, class: el.className, style: el.getAttribute('style') })
    }
    img.onerror = () => {
      callback(new Error(`Image at ${src} could not be loaded for parallax.`))
    }
  }
  /**
   * @suppress {checkTypes}
   */
  static get 'plain'() {
    return true
  }
  render({ img, speedy = 0.5, speedx = 0, src, once = false,
    'translate-x': translateX = 0, 'translate-y': translateY = 0, 'no-blur': noBlur }) {
    if (this.firstRender) {
      this.img = img
      // this.handle.className += ' RunFadeIn'
      if (!noBlur) {
        this.handle.style.animation = this.handle.style.webkitAnimation = `_unblurIn ${1000}ms ease-out`
      }
      this.firstRender = false
      this.translateX = translateX
      this.translateY = translateY
    }
    this.once = once
    this._speedy = parseFloat(speedy)
    this._speedx = parseFloat(speedx)

    this.listener = () => {
      this.compute()
    }
    window.addEventListener('scroll', this.listener)
    // window.addEventListener('resize', this.listener)
    this.handle.style['background-image'] = `url(${src})`
    this.compute()
  }
}

// export const plain = true

const floatToPx = (f) => {
  return `${f.toFixed()}px`
}

const changeValues = (timeout, interval, array, cb, completeCb = null) => {
  const dd = [], currents = [], tos = []
  for (const [from,to,label] of array) {
    console.log('Will change %s from %s to %s', label, from, to)
    const d = (to - from) / (timeout/interval)
    dd.push(d)
    currents.push(from)
    tos.push(to)
  }
  poll(timeout, interval, () => {
    for (let i = 0; i < dd.length; i++) {
      const d = dd[i]
      currents[i] += d
      cb(currents)
    }
  }, () => {
    cb(tos)
    if (completeCb) completeCb(tos)
  })
}

const px = (v) => {
  return `${v}px`
}

/**
 * Execute given function at interval during given time frame.
 * @param {number} timeout After how long to clear polling.
 * @param {number} interval The interval with which to poll.
 * @param {!Function} cb The callback function to execute during each cycle.
 * @param {Function} completeCb Funciton to call when interval is cleared.
 */
const poll = (timeout, interval, cb, completeCb = null) => {
  const i = setInterval(cb, interval)
  const t = setTimeout(() => {
    clearInterval(i)
    if (completeCb) completeCb()
  }, timeout)
  return () => {
    clearTimeout(t)
    clearInterval(i)
  }
}

// window.addAdditionalScroll = (y) => {
//   if (y === null) this.additionalY = 0
//   this.additionalY += y
//   this.additionY = Math.max(this.additionalY, 200)
//   this.compute()
// }